// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import XngCarousel, { SwiperInstance, SwiperStyle } from "@werner/App/Components/XngCarousel";
import { SLIDES_PER_VIEW_POINTS } from "@werner/XDL/constants";
import { color, generateSlidesPerViewMap, xScreen } from "@werner/XDL/Utilities";
import React, { createRef, ReactNode, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    attributeWrapper?: InjectableCss;
    attributeContainer?: InjectableCss;
    attributeLabel?: TypographyProps;
    attributeValue?: TypographyProps;
    attributesCarouselExtendedStyles?: SwiperStyle;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },

    carouselCellGridItem: {
        width: 8,
        css: css`
            &&&& {
                flex-basis: 100%;
                max-width: 100%;
                margin: 0 auto;
                .swiper-slide {
                    // to make all slider same height
                    height: auto;
                    // To fix swiper render issue
                    margin-top: 1px;
                    margin-bottom: 1px;
                    max-width: 308px;
                    ${xScreen("lte", "sm")} {
                        max-width: 100%;
                    }
                }
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
    },
    attributeWrapper: {
        css: css`
            margin-top: 40px;
            width: 100%;
            max-width: 100%;
            padding-top: 20px;
            border-top: 1px solid ${color("common.border", 0.5)};
        `,
    },
    attributeContainer: {
        css: css`
            width: 100%;
            max-width: 100%;
            border-radius: 4px;
            background-color: ${color("common.white")};
            // box-shadow: 0 0 9px 0 ${color("primary", 0.15)};
        `,
    },
    attributeLabel: {
        as: "p",
        size: 15,
        weight: "bold",
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            padding: 10px 10px 10px 0px;
            // background: ${getColor("primary")};
            background: ${color("common.white")};
            min-height: 36px;
            &.first-item {
                border-radius: 4px 4px 0 0;
            }
        `,
    },
    attributeValue: {
        as: "p",
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            text-align: center;
            min-height: 65px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    attributesCarouselExtendedStyles: {
        container: {
            css: css`
                background: ${color("common.ui2")};
            `,
        },
    },
};

const Carousel = ({ id, extendedStyles, title, hideTitle, maxNumberOfColumns, slideDetails }: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));
    // XNG-Changes
    const [attributeSwipers, setAttributeSwipers] = useState<SwiperInstance[] | undefined>(undefined);
    const [productSwiper, setProductSwiper] = useState<SwiperInstance | undefined>(undefined);

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return <>&nbsp;</>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    // <XNG-Changes>
    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: 2,
        [SLIDES_PER_VIEW_POINTS.sm]: 3,
        [SLIDES_PER_VIEW_POINTS.md]: 4,
        [SLIDES_PER_VIEW_POINTS.lg]: 5,
    });

    // XNG-Changes : changing swiper to use XngCarousel
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <GridContainer {...styles.mainContainer} data-test-selector="productCompareCarousel">
                <GridItem {...styles.carouselCellGridItem}>
                    <XngCarousel
                        controller={{ control: attributeSwipers, by: "container" }}
                        onSwiper={setProductSwiper}
                        onBeforeDestroy={setProductSwiper}
                        breakpoints={breakpoints}
                        draggable={isTouchDevice()}
                    >
                        {slideDetails.slides.map(slide => (
                            <React.Fragment key={slide.id}>{slide.renderComponent({})}</React.Fragment>
                        ))}
                    </XngCarousel>
                </GridItem>
                <GridItem {...styles.carouselCellGridItem}>
                    <StyledWrapper {...styles.attributeWrapper}>
                        {slideDetails.attributeTypes.map((attribute, index) => (
                            <StyledWrapper key={attribute.id} {...styles.attributeContainer}>
                                <Typography {...styles.attributeLabel} className={`${index === 0 ? "first-item" : ""}`}>
                                    {attribute.label.toUpperCase() || attribute.name.toUpperCase()}
                                </Typography>
                                <XngCarousel
                                    onSwiper={swiper =>
                                        setAttributeSwipers(prevState => [...(prevState || []), swiper])
                                    }
                                    controller={{ control: productSwiper, by: "container" }}
                                    onBeforeDestroy={swiper => {
                                        setAttributeSwipers(prevState => [...(prevState || []), swiper]);
                                        if (productSwiper && !productSwiper.destroyed) {
                                            productSwiper.controller.control = undefined;
                                        }
                                    }}
                                    pagination={false}
                                    navigation={false}
                                    breakpoints={breakpoints}
                                    draggable={isTouchDevice()}
                                    extendedStyles={styles.attributesCarouselExtendedStyles}
                                >
                                    {slideDetails.products.map((product, index) => (
                                        <StyledWrapper
                                            key={`attribute_value_for_${product.id}`}
                                            css={css`
                                                overflow: hidden;
                                                ${xScreen("lte", "xs")} {
                                                    border-left: 0;
                                                }
                                            `}
                                        >
                                            <Typography {...styles.attributeValue}>
                                                {getProductAttributeValue(product, attribute)}
                                            </Typography>
                                        </StyledWrapper>
                                    ))}
                                </XngCarousel>
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
